import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  InjectionToken,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import { routes } from './app.routes';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideState, provideStore } from '@ngrx/store';
import { donationFlowReducer } from 'features/src/lib/donations/state/donation-flow/reducer';
import { provideEffects } from '@ngrx/effects';
import { DonationFlowEffects } from 'features/src/lib/donations/state/donation-flow/effects';

import { provideStoreDevtools } from '@ngrx/store-devtools';
import { isDevMode } from '@angular/core';
import { campaignsReducer } from 'features/src/lib/donations/state/campaing/reducer';
import { CampaignsEffects } from 'features/src/lib/donations/state/campaing/effects';
import { PaymentElementEffects } from 'features/src/lib/donations/state/payment/effects';
import { PaymentElementReducer } from 'features/src/lib/donations/state/payment/reducer';
import { donationsFeedFeature } from 'features/src/lib/donations/state/donations-feed/selectors';
import { DonationsFeedEffects } from 'features/src/lib/donations/state/donations-feed/effects';
import {
  DONATION_PLANS,
  DEFAULT_DONATION_PLANS,
} from 'features/src/lib/donations/types/donation-plans';
import { ErrorInterceptor } from './core/errors/error.interceptor';
import { GlobalErrorHandler } from './core/errors/global-error.handler';
import { donationFlowPersistMetaReducer } from 'features/src/lib/donations/meta-reducers/persistDonationFlow';
import { AppState } from 'features/src/lib/donations/types/app.state';
import { environment } from 'src/environments/environment';
import { PaymentAlertEffects } from 'features/src/lib/donations/state/payment-alert/effects';
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    './assets/locales/',
    '.translation.json'
  );
}

export interface CrezcoDonationAppRuntimeConfig {
  apiBaseUrl: string;
  controller: string;
  signalRNegotiateUrl: string;
  stripePublicKey?: string;
}

export const CREZCO_DONATION_APP_CONFIG =
  new InjectionToken<CrezcoDonationAppRuntimeConfig>(
    'CREZCO_DONATION_APP_CONFIG'
  );

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideStore<AppState>(
      {
        campaigns: campaignsReducer,
        donationFlow: donationFlowReducer,
        paymentElement: PaymentElementReducer,
      },
      { metaReducers: [donationFlowPersistMetaReducer] }
    ),
    provideState(donationsFeedFeature),
    provideEffects([
      DonationsFeedEffects,
      CampaignsEffects,
      DonationFlowEffects,
      PaymentElementEffects,
      PaymentAlertEffects,
    ]),
    {
      provide: CREZCO_DONATION_APP_CONFIG,
      useValue: {
        apiBaseUrl: `${environment.apiBaseUrl}/api/`,
        controller: environment.controller,
        stripePublicKey: environment.stripePublicKey,
        signalRNegotiateUrl: environment.signalRNegotiateUrl,
      },
    },
    { provide: DONATION_PLANS, useValue: DEFAULT_DONATION_PLANS },
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(), // Solo lectura en prod
      autoPause: true,
      trace: false,
    }),
  ],
};
